import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Spinner } from 'react-bootstrap';
import SettingsSelect from '../SettingsSelect';
import { Percent, Calculator, ExternalLink, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

/**
 * Avdragsprofil för blandad verksamhet (G9).
 *
 * A business with both momspliktig and momsfri verksamhet may deduct only the
 * taxable side's share of shared-cost input VAT. The profile is per
 * räkenskapsår, because the omsättningsbaserade andel is a yearly proportion.
 *
 * The percentage the tenant applies is always the tenant's. The ledger's own
 * proportion is offered as a proposal beside it, never silently substituted —
 * ML 13 kap. 29 § explicitly allows another fördelningsgrund (yta, tid,
 * lönekostnad), and an override you can see is auditable.
 */

const METHOD_OPTIONS = [
    { value: 'full', label: 'Full avdragsrätt — all ingående moms dras av' },
    { value: 'pro_rata', label: 'Blandad verksamhet — bara en andel är avdragsgill' },
];

const PRO_RATA_OPTIONS = [
    {
        value: 'omsattningsbaserad',
        label: 'Omsättningsbaserad fördelning',
        hint: 'Andelen momspliktig omsättning av total omsättning. HFD 2023 (mål 7254-7255-22) '
            + 'slår fast att den här metoden i momsdirektivet har direkt effekt — du får alltid välja den.',
    },
    {
        value: 'skalig_grund',
        label: 'Skälig grund (annan fördelningsnyckel)',
        hint: 'Fördelning efter till exempel yta, tid eller lönekostnad (ML 13 kap. 29 §). '
            + 'Ange vilken grund du använder — det är den som ska kunna försvaras vid en revision.',
    },
];

// R-MIX-3 / R-MIX-2 in the register; on screen the link is named by its
// publisher, never by our internal rule id.
// Amounts in this panel run to six and seven figures, where an unspaced
// "608500 kr" has to be counted rather than read.
const formatPercent = (value) => new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 2,
}).format(Number(value) || 0);

const formatKr = (value) => new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 0,
}).format(Math.round(Number(value) || 0));

const RULE_LINKS = {
    omsattningsbaserad: {
        label: 'Skatteverkets rättsliga vägledning',
        url: 'https://www4.skatteverket.se/rattsligvagledning/edition/2023.16/438980.html',
    },
    skalig_grund: {
        label: 'Skatteverket om avdrag för ingående moms',
        url: 'https://www.skatteverket.se/foretag/moms/kopavarorochtjanster/kopavarorellertjanstertillforetaget.4.7459477810df5bccdd480005156.html',
    },
};

export default function VatDeductionProfileSection({ companyId, fiscalYears = [] }) {
    const yearOptions = useMemo(() => fiscalYears.map((year) => ({
        value: year.fiscal_year_id || year.fiscalYearId,
        label: `${year.start_date || year.startDate} – ${year.end_date || year.endDate}`,
    })), [fiscalYears]);

    const [selectedYear, setSelectedYear] = useState(null);
    const [profile, setProfile] = useState(null);
    const [proportion, setProportion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [computing, setComputing] = useState(false);

    const [method, setMethod] = useState('full');
    const [percent, setPercent] = useState('');
    const [proRataMethod, setProRataMethod] = useState('omsattningsbaserad');
    const [basisNote, setBasisNote] = useState('');

    useEffect(() => {
        if (!selectedYear && yearOptions.length > 0) setSelectedYear(yearOptions[0]);
    }, [yearOptions, selectedYear]);

    const load = useCallback(async (fiscalYearId) => {
        if (!fiscalYearId) return;
        setLoading(true);
        setProportion(null);
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/reporting/vat-deduction-profile`),
                { params: { fiscal_year_id: fiscalYearId }, withCredentials: true }
            );
            const loaded = response.data.profile;
            setProfile(loaded);
            setMethod(loaded.method);
            setPercent(loaded.method === 'pro_rata' ? String(loaded.deductible_percent) : '');
            setProRataMethod(loaded.pro_rata_method || 'omsattningsbaserad');
            setBasisNote(loaded.basis_note || '');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Avdragsprofilen kunde inte hämtas.');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => { if (selectedYear) load(selectedYear.value); }, [selectedYear, load]);

    const computeProportion = async () => {
        setComputing(true);
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/reporting/vat-deduction-profile/proportion`),
                { params: { fiscal_year_id: selectedYear.value }, withCredentials: true }
            );
            setProportion(response.data.proportion);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Andelen kunde inte beräknas.');
        } finally {
            setComputing(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const response = await axios.put(
                getApiUrl(`${companyId}/reporting/vat-deduction-profile`),
                {
                    fiscal_year_id: selectedYear.value,
                    method,
                    deductible_percent: method === 'pro_rata' ? Number(percent) : null,
                    pro_rata_method: method === 'pro_rata' ? proRataMethod : null,
                    basis_note: method === 'pro_rata' ? basisNote : null,
                },
                { withCredentials: true }
            );
            setProfile(response.data.profile);
            const touched = (response.data.periods || []).filter((p) => p.recorded || p.removed).length;
            toast.success(
                touched > 0
                    ? `Avdragsprofilen sparad. ${touched} öppen period räknades om.`
                    : 'Avdragsprofilen sparad.'
            );
        } catch (error) {
            toast.error(error.response?.data?.message || 'Avdragsprofilen kunde inte sparas.');
        } finally {
            setSaving(false);
        }
    };

    const selectedProRata = PRO_RATA_OPTIONS.find((option) => option.value === proRataMethod);
    const ruleLink = RULE_LINKS[proRataMethod];

    return (
        <div className="modern-card" id="momsavdrag">
            <div className="modern-card-header">
                <div>
                    <h2 className="section-title">
                        <Percent size={16} />
                        Avdragsprofil för moms
                    </h2>
                    <p className="section-subtitle">
                        Blandad verksamhet — hur stor andel av den ingående momsen som är avdragsgill,
                        per räkenskapsår.
                    </p>
                </div>
            </div>

            <div className="modern-card-body">
                {yearOptions.length === 0 ? (
                    <div className="empty-state">
                        <Percent size={32} className="empty-icon" />
                        <h4>Inga räkenskapsår</h4>
                        <p>Skapa ett räkenskapsår innan du ställer in avdragsprofilen.</p>
                    </div>
                ) : (
                    <>
                        <div className="row g-3 mb-3">
                            <div className="col-md-5">
                                <label htmlFor="vatProfileYear" className="modern-label">Räkenskapsår</label>
                                <SettingsSelect
                                    id="vatProfileYear"
                                    options={yearOptions}
                                    value={selectedYear?.value ?? null}
                                    onChange={(value) => setSelectedYear(
                                        yearOptions.find((o) => o.value === value) || null
                                    )}
                                    isDisabled={loading || saving}
                                />
                            </div>
                            <div className="col-md-7">
                                <label htmlFor="vatProfileMethod" className="modern-label">Avdragsrätt</label>
                                <SettingsSelect
                                    id="vatProfileMethod"
                                    options={METHOD_OPTIONS}
                                    value={method}
                                    onChange={setMethod}
                                    isDisabled={loading || saving}
                                />
                            </div>
                        </div>

                        {loading && <Spinner animation="border" size="sm" />}

                        {method === 'pro_rata' && (
                            <>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label htmlFor="vatProfilePercent" className="modern-label">Avdragsgill andel (%)</label>
                                        <input
                                            id="vatProfilePercent"
                                            type="number"
                                            className="modern-input"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={percent}
                                            onChange={(event) => setPercent(event.target.value)}
                                            disabled={saving}
                                        />
                                    </div>
                                    <div className="col-md-8">
                                        <label htmlFor="vatProfileProRata" className="modern-label">Fördelningsmetod</label>
                                        <SettingsSelect
                                            id="vatProfileProRata"
                                            options={PRO_RATA_OPTIONS}
                                            value={selectedProRata?.value ?? null}
                                            onChange={setProRataMethod}
                                            isDisabled={saving}
                                        />
                                    </div>
                                </div>

                                {selectedProRata && (
                                    <p className="section-subtitle mb-3">
                                        <Info size={14} className="me-1" />
                                        {selectedProRata.hint}
                                        {ruleLink && (
                                            <>
                                                {' '}
                                                <a href={ruleLink.url} target="_blank" rel="noopener noreferrer">
                                                    {ruleLink.label} <ExternalLink size={12} />
                                                </a>
                                            </>
                                        )}
                                    </p>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Fördelningsgrund
                                        {proRataMethod === 'skalig_grund' && ' (obligatorisk)'}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        maxLength={1000}
                                        placeholder="Till exempel: 62 % av lokalytan används i den momspliktiga verksamheten."
                                        value={basisNote}
                                        onChange={(event) => setBasisNote(event.target.value)}
                                        disabled={saving}
                                    />
                                </Form.Group>

                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={computeProportion}
                                        disabled={computing || saving}
                                        className="d-flex align-items-center gap-2"
                                    >
                                        <Calculator size={16} />
                                        {computing ? 'Beräknar…' : 'Beräkna andel från bokföringen'}
                                    </Button>
                                    {profile?.computed_percent !== null && profile?.computed_percent !== undefined && (
                                        <Badge bg="" style={{ background: 'var(--rgba-primary-8, #2f5e37)' }}>
                                            Bokföringen: {profile.computed_percent} %
                                        </Badge>
                                    )}
                                </div>

                                {proportion && (
                                    <div className="alert alert-light" role="status">
                                        <strong>
                                            {proportion.percent === null
                                                ? 'Ingen omsättning att räkna på under året.'
                                                : `Bokföringen ger ${formatPercent(proportion.percent)} % avdragsgill andel.`}
                                        </strong>
                                        {proportion.percent !== null && (
                                            <div className="section-subtitle mb-2">
                                                Momspliktig omsättning {formatKr(proportion.taxable_turnover)} kr av
                                                {' '}{formatKr(proportion.taxable_turnover + proportion.exempt_turnover)} kr totalt.
                                            </div>
                                        )}
                                        {/* R-MIX-5: with total årsomsättning as fördelningsnyckel
                                            the andel MAY be rounded up to the next whole percent
                                            (art. 175.1). "Får", not "ska" — so both figures are
                                            offered and the exact one stays choosable. Other
                                            fördelningsnycklar get no round-up at all, which is why
                                            this appears only in the turnover calculation. */}
                                        {proportion.rounding_note && (
                                            <div className="section-subtitle mb-2">
                                                {proportion.rounding_note}
                                            </div>
                                        )}
                                        <div className="section-subtitle mb-2">{proportion.caveat}</div>
                                        {proportion.percent !== null && (
                                            <div className="d-flex gap-2 flex-wrap">
                                                {proportion.rounding_note && (
                                                    <Button
                                                        size="sm"
                                                        style={{ background: 'var(--primary)' }}
                                                        className="border-0"
                                                        onClick={() => setPercent(String(proportion.percent_rounded_up))}
                                                    >
                                                        Använd {proportion.percent_rounded_up} %
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => setPercent(String(proportion.percent))}
                                                >
                                                    Använd {formatPercent(proportion.percent)} %
                                                    {proportion.rounding_note ? ' (exakt)' : ''}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        <div className="action-buttons">
                            <Button
                                variant="primary"
                                onClick={save}
                                disabled={saving || loading || !selectedYear}
                                className="d-flex align-items-center gap-2"
                            >
                                {saving ? 'Sparar…' : 'Spara avdragsprofil'}
                            </Button>
                        </div>

                        <p className="section-subtitle mt-3">
                            När profilen sparas räknas alla <strong>öppna</strong> momsperioder i
                            räkenskapsåret om, och den icke avdragsgilla delen dras från fält 48.
                            Stängda perioder rörs inte — de är lämnade under den profil som gällde då,
                            och en ändring där är en rättelse.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
